import React from 'react';

interface TermsOfServicePageProps {
  onBack: () => void;
}

const TermsOfServicePage: React.FC<TermsOfServicePageProps> = ({ onBack }) => {
  const lastUpdated = '24 November 2025';

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={onBack}
            className="flex items-center text-primary-600 hover:text-primary-700 mb-4 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Dashboard
          </button>
          <h1 className="text-3xl font-bold text-midnight">Terms of Service</h1>
          <p className="text-gray-600 mt-2">Last updated: {lastUpdated}</p>
        </div>

        <div className="bg-white rounded-xl shadow-soft p-8 space-y-8">
          {/* Introduction */}
          <section>
            <h2 className="text-xl font-semibold text-midnight mb-4">Who we are and how these terms work</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              AI Marker Mate is an Australian software service that helps teachers mark history essays and give clear,
              rubric-aligned feedback. By accessing or using the service, you agree to these Terms of Service. If you are
              using AI Marker Mate on behalf of a school or organisation, you confirm you are authorised to accept these
              terms for that organisation.
            </p>
            <p className="text-gray-700 leading-relaxed">
              These terms are designed for a small Australian team supporting Australian educators and should be read
              together with our Privacy Policy. Australian Consumer Law rights continue to apply.
            </p>
          </section>

          {/* Service scope */}
          <section>
            <h2 className="text-xl font-semibold text-midnight mb-4">Our service</h2>
            <ul className="text-gray-700 leading-relaxed space-y-3 list-disc list-inside">
              <li>We provide an AI-assisted marking and feedback workflow for history essays.</li>
              <li>Outputs are guidance only; teachers remain responsible for final marks and professional judgement.</li>
              <li>We may improve or refine features over time. We will communicate material changes where practicable.</li>
              <li>We aim to keep services available, but occasional maintenance or outages may occur.</li>
            </ul>
          </section>

          {/* Eligibility and accounts */}
          <section>
            <h2 className="text-xl font-semibold text-midnight mb-4">Eligibility and accounts</h2>
            <ul className="text-gray-700 leading-relaxed space-y-3 list-disc list-inside">
              <li>The service is intended for educators and schools in Australia.</li>
              <li>You are responsible for safeguarding login credentials and account activity.</li>
            </ul>
          </section>

          {/* Data and privacy */}
          <section>
            <h2 className="text-xl font-semibold text-midnight mb-4">Student data, privacy, and residency</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We handle personal information in line with the Australian Privacy Principles and our{' '}
              <a href="#/privacy" className="text-primary-600 hover:text-primary-700 underline">
                Privacy Policy
              </a>
              . Key points:
            </p>
            <ul className="text-gray-700 leading-relaxed space-y-3 list-disc list-inside">
              <li>Primary storage is in Australia (GCP australia-southeast1). Some processing may occur overseas via AI providers.</li>
              <li>Do not upload unnecessary personal information. Prefer student numbers instead of full names.</li>
              <li>You must have authority and any required consents to provide student data to us.</li>
              <li>We work with vetted processors (e.g., Google, OpenAI, Anthropic) under privacy and security obligations.</li>
            </ul>
          </section>

          {/* Acceptable use */}
          <section>
            <h2 className="text-xl font-semibold text-midnight mb-4">Acceptable use</h2>
            <ul className="text-gray-700 leading-relaxed space-y-3 list-disc list-inside">
              <li>Use the service lawfully and only for educational assessment and feedback.</li>
              <li>Do not upload content that is unlawful, harmful, discriminatory, or infringing.</li>
              <li>Do not try to bypass security controls, misuse credits, or disrupt other users.</li>
              <li>Respect academic integrity policies; the service must not be used to help students cheat.</li>
            </ul>
          </section>

          {/* Credits and billing */}
          <section>
            <h2 className="text-xl font-semibold text-midnight mb-4">Credits, fees, and billing</h2>
            <ul className="text-gray-700 leading-relaxed space-y-3 list-disc list-inside">
              <li>Some features consume credits. Pricing is in AUD unless stated otherwise.</li>
              <li>Credit purchases follow our Refund Policy and any terms shown at checkout.</li>
              <li>Taxes may apply. You authorise us or our payment processor to charge the selected payment method.</li>
            </ul>
          </section>

          {/* Intellectual property */}
          <section>
            <h2 className="text-xl font-semibold text-midnight mb-4">Intellectual property and licences</h2>
            <ul className="text-gray-700 leading-relaxed space-y-3 list-disc list-inside">
              <li>You retain ownership of the content you upload. You grant us a licence to use it to provide and improve the service.</li>
              <li>We own the platform, models, and documentation. Do not copy, resell, or reverse engineer the service.</li>
              <li>We may use de-identified or aggregated data for quality, safety, and product improvement in line with our Privacy Policy.</li>
            </ul>
          </section>

          {/* Service quality and disclaimers */}
          <section>
            <h2 className="text-xl font-semibold text-midnight mb-4">Service quality, AI outputs, and limitations</h2>
            <ul className="text-gray-700 leading-relaxed space-y-3 list-disc list-inside">
              <li>AI outputs can contain errors or bias. Always review before relying on them.</li>
              <li>We provide the service with reasonable care and skill, but availability is not guaranteed.</li>
              <li>To the extent permitted by law, our liability is limited to re-supplying the services or paying the cost of re-supply.</li>
              <li>Nothing limits your rights under the Australian Consumer Law.</li>
            </ul>
          </section>

          {/* Suspension and termination */}
          <section>
            <h2 className="text-xl font-semibold text-midnight mb-4">Suspension and termination</h2>
            <ul className="text-gray-700 leading-relaxed space-y-3 list-disc list-inside">
              <li>You can stop using the service at any time.</li>
              <li>We may suspend or terminate access for breach, misuse, non-payment, or risk to the service or others.</li>
              <li>Data handling after termination follows our Privacy Policy and applicable retention obligations.</li>
            </ul>
          </section>

          {/* Governing law */}
          <section>
            <h2 className="text-xl font-semibold text-midnight mb-4">Governing law and contact</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              These terms are governed by the laws of New South Wales, Australia. Any disputes are subject to the courts of New South Wales (and, where relevant,
              applicable Commonwealth laws).
            </p>
          </section>

          {/* Footer notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <p className="text-blue-800 text-sm">
              <strong>This page was last updated on {lastUpdated}.</strong> We will notify users of material changes where practicable. Continued use after changes
              take effect means you accept the updated terms.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
